import { DateTime } from 'luxon';

import type { Timestep } from 'app-components/utils/date';

import type { CellItem, Report, ReportRow } from './generatedReportsTypes';

export const getReportRowUtil = (
  report?: Report | null,
  entityType?: string,
  entityId?: string,
  parentId?: string,
): ReportRow | undefined => {
  if (!report) {
    return undefined;
  }
  return report.rows.find(
    (row) => row.entityType === entityType && row.entityId === entityId && (!parentId || row.parentId === parentId),
  );
};

/**
 * Look a cell up by the *header* id of its column.
 *
 * `headerValue.id` is only a usable key while it is unique across a report's columns, which holds
 * for SUMMARY and TRANSACTION but not for COMPARISON: every column in the Total group carries
 * `headerValue.id === 'Total'`, so a `find` returns whichever happens to come first — silently, and
 * with a plausible-looking number. Comparison callers must address a column by its own `columnId`
 * (see `comparisonColumnModel.findCellForColumn`), so this returns nothing for them rather than
 * guessing. Today's callers are all TaxPlanner reading TAX_SUMMARY, so nothing reaches this path.
 */
export const getReportCellValueUtil = (
  report?: Report | null,
  columnHeaderId?: string,
  entityType?: string,
  entityId?: string,
  parentId?: string,
): string | undefined => {
  if (!report) {
    return undefined;
  }
  if (report.configuration?.reportType === 'COMPARISON' || report.reportType === 'COMPARISON') {
    return undefined;
  }
  const column = report.columns.find(
    (column) => 'id' in column.headerValue && column.headerValue.id === columnHeaderId,
  );
  if (!column?.id) {
    return undefined;
  }
  const row = getReportRowUtil(report, entityType, entityId, parentId);
  if (!row?.cellData) {
    return undefined;
  }
  const cell = row.cellData.find((cell) => cell.columnId === column.id);
  if (!cell) {
    return undefined;
  }
  return cell.value;
};

export const getReportCellNumberValueUtil = (
  report?: Report | null,
  columnHeaderId?: string,
  entityType?: string,
  entityId?: string,
  parentId?: string,
): number | undefined => {
  const valueString = getReportCellValueUtil(report, columnHeaderId, entityType, entityId, parentId);
  // `Number(null)` and `Number('')` are both 0, so an absent or blank cell would otherwise report a
  // real zero. Absent and zero have to stay distinguishable: a range that has ended has no value at
  // all, which is not the same claim as "this category totalled nothing".
  if (valueString == null || valueString.trim() === '') {
    return undefined;
  }
  const value = Number(valueString);
  return isNaN(value) ? undefined : value;
};

// Stable identity key for a report row/tree node: entityId when present, falling back to id.
// Display names (row.name) are not guaranteed unique (e.g. a mortgage loan named after the same
// address as the property it's secured against), so anything that needs to key on "this specific
// row" — chart fields, filter sets, lookups — must go through this single definition to stay
// consistent with each other.
export const getReportEntityKey = (row: { entityId?: string; id?: string }): string | undefined =>
  row.entityId || row.id;

export const groupReportRowsByParentId = (rows: ReportRow[]): Record<string, ReportRow[]> =>
  rows.reduce<Record<string, ReportRow[]>>((groups, row) => {
    const key = row.parentId ?? '-1';
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(row);
    return groups;
  }, {});

/**
 * Canonical cache key for one reconciliation attempt over `transactionIds`.
 *
 * Order-independent, because the same drilldown can hand us the same ids in a different order and
 * must not be fetched twice. Sorted with the default comparator rather than `localeCompare`: these
 * ids are assembled at runtime from `txnId`, `splitItemId` and `entityId` on a server payload whose
 * string typing is optimistic, and the default comparator stringifies its operands, so a malformed
 * entry yields a useless-but-stable key instead of throwing mid-sort. Locale collation would buy
 * nothing anyway — the key is only ever compared against itself.
 */
export const getReconciliationAttemptKey = (transactionIds: readonly string[]): string =>
  JSON.stringify([...transactionIds].sort());

/** Returns parent transaction IDs, optionally limited to referenced parent or split item IDs. */
export const getReportTransactionIds = (
  report?: Pick<Report, 'rows'> | null,
  referencedIds?: ReadonlySet<string>,
): string[] => {
  const transactionIds = new Set<string>();

  report?.rows.forEach((row) => {
    let foundCellTransaction = false;
    row.cellData?.forEach((cell) => {
      cell.items?.forEach((item) => {
        const isReferenced =
          !referencedIds ||
          (item.txnId ? referencedIds.has(item.txnId) : false) ||
          (item.splitItemId ? referencedIds.has(item.splitItemId) : false);
        if (item.txnId && isReferenced) {
          transactionIds.add(item.txnId);
          foundCellTransaction = true;
        }
      });
    });

    if (
      !foundCellTransaction &&
      row.entityType === 'TRANSACTION_TYPE' &&
      row.entityId &&
      (!referencedIds || referencedIds.has(row.entityId))
    ) {
      transactionIds.add(row.entityId);
    }
  });

  return Array.from(transactionIds);
};

export const getReportDrilldownTransactionIds = (
  report: Pick<Report, 'reportType' | 'rows'> | null | undefined,
  {
    columnId,
    entityId,
    rowId,
  }: {
    columnId?: string;
    entityId?: string;
    rowId?: string;
  },
): string[] => {
  if (!report) return [];

  const rowsByParentId = groupReportRowsByParentId(report.rows);
  const targetRow =
    (rowId ? report.rows.find((row) => row.id === rowId) : undefined) ??
    report.rows.find((row) => row.entityId === entityId);
  if (!targetRow) return [];

  const referencedIds = new Set<string>();
  const visitedRows = new Set<ReportRow>();
  const collectRow = (row: ReportRow): void => {
    if (visitedRows.has(row)) return;
    visitedRows.add(row);
    if (row.entityType === 'TRANSACTION_TYPE' && row.entityId) {
      referencedIds.add(row.entityId);
    }

    const cells =
      columnId === undefined
        ? row.cellData
        : row.cellData.filter((cell, index) => cell.columnId === columnId || index === Number(columnId));
    cells.forEach((cell) => {
      cell.items?.forEach((item) => {
        if (item.txnId) referencedIds.add(item.txnId);
        if (item.splitItemId) referencedIds.add(item.splitItemId);
      });
    });

    if (report.reportType === 'TRANSACTION' || row.isTotal) {
      rowsByParentId[row.id ?? '']?.forEach(collectRow);
      row.childrenEntityIds?.forEach((childEntityId) => {
        const child = report.rows.find((candidate) => candidate.entityId === childEntityId);
        if (child) collectRow(child);
      });
    }
  };

  collectRow(targetRow);
  return getReportTransactionIds(report, referencedIds);
};

const getReportRowTotal = (row: ReportRow): number =>
  row.cellData?.reduce((sum, cell) => {
    const value = Number(cell.value || 0);
    return isNaN(value) ? sum : sum + value;
  }, 0) ?? 0;

const collectTransactionDescendants = (
  row: ReportRow,
  rowsByParentId?: Record<string, ReportRow[]>,
  acc: ReportRow[] = [],
): ReportRow[] => {
  if (row.entityType === 'TRANSACTION_TYPE') {
    acc.push(row);
  }

  if (row.id) {
    rowsByParentId?.[row.id]?.forEach((child) => {
      collectTransactionDescendants(child, rowsByParentId, acc);
    });
  }

  return acc;
};

const getTransactionItemId = (item: CellItem): string | undefined => item.splitItemId || item.txnId;

const collectTransactionItemIds = (
  row: ReportRow,
  rowsByParentId?: Record<string, ReportRow[]>,
  acc: string[] = [],
): string[] => {
  if (row.entityType === 'TRANSACTION_TYPE' && row.entityId) {
    acc.push(row.entityId);
  }

  row.cellData?.forEach((cell) => {
    cell.items?.forEach((item) => {
      const id = getTransactionItemId(item);
      if (id) {
        acc.push(id);
      }
    });
  });

  if (row.id) {
    rowsByParentId?.[row.id]?.forEach((child) => {
      collectTransactionItemIds(child, rowsByParentId, acc);
    });
  }

  return acc;
};

export const shouldHideExcludedZeroTotalReportRow = ({
  row,
  excludedTransactionIds,
  rowsByParentId,
  showExcludedTxns,
}: {
  row: ReportRow;
  excludedTransactionIds: ReadonlySet<string>;
  rowsByParentId?: Record<string, ReportRow[]>;
  showExcludedTxns: boolean;
}): boolean => {
  if (showExcludedTxns || Math.abs(getReportRowTotal(row)) >= 0.005) {
    return false;
  }

  const transactionRows = collectTransactionDescendants(row, rowsByParentId);
  if (transactionRows.length > 0) {
    return transactionRows.every((txnRow) => txnRow.isExcludedFromReports);
  }

  const transactionItemIds = collectTransactionItemIds(row, rowsByParentId);
  return transactionItemIds.length > 0 && transactionItemIds.every((id) => excludedTransactionIds.has(id));
};

const formatMonthDay = (date: DateTime, includeYear: boolean): string =>
  date?.isValid ? date.toFormat(`M/d${includeYear ? '/yy' : ''}`) : '';
const formatMonthName = (date: DateTime, includeYear: boolean): string =>
  date?.isValid ? date.toFormat(`LLLL${includeYear ? ' yyyy' : ''}`) : '';
const formatWeekName = (date: DateTime, includeYear: boolean): string =>
  date?.isValid ? date.toFormat(`LLL d${includeYear ? ', yyyy' : ''}`) : '';
const formatYearName = (date: DateTime): string => (date?.isValid ? date.toFormat('LLL d') : '');
const formatShortMonth = (date: DateTime): string => (date?.isValid ? date.toFormat('LLL') : '');

export const getDateStringForTimestepAndRange = (
  startOn: string,
  endOn: string,
  report: {
    configuration?: {
      timestep?: Timestep;
    };
    startOn?: string;
    endOn?: string;
  },
  options: { forceYear?: boolean } = {},
): string => {
  const startOnDate = DateTime.fromISO(startOn);
  const endOnDate = DateTime.fromISO(endOn);
  const reportTimestep: Timestep = report.configuration?.timestep ?? 'MONTH';
  const reportStartDate = report.startOn ? DateTime.fromISO(report.startOn) : startOnDate;
  const reportEndDate = report.endOn ? DateTime.fromISO(report.endOn) : endOnDate;
  const hasMultipleYears =
    options.forceYear === true ||
    (reportStartDate?.isValid && reportEndDate?.isValid ? reportStartDate.year !== reportEndDate.year : false);
  const showYear = reportTimestep === 'WEEK' || hasMultipleYears;

  switch (reportTimestep) {
    case 'DAY':
      return formatMonthDay(startOnDate, showYear);
    case 'WEEK':
      return `${formatWeekName(startOnDate, hasMultipleYears)} - ${formatWeekName(endOnDate, hasMultipleYears) || ''}`;
    case 'MONTH':
      return formatMonthName(startOnDate, showYear);
    case 'QUARTER':
      return `${formatShortMonth(startOnDate)} - ${formatShortMonth(endOnDate) || ''}${
        showYear ? ` ${startOnDate?.year}` : ''
      }`;
    case 'YEAR': {
      const today = DateTime.now();
      const fullYear =
        startOnDate?.isValid &&
        endOnDate?.isValid &&
        startOnDate.year === endOnDate.year &&
        startOnDate.day === 1 &&
        startOnDate.month === 1 &&
        ((endOnDate.day === 31 && endOnDate.month === 12) ||
          (endOnDate.day === today.day && endOnDate.month === today.month));
      return fullYear
        ? String(startOnDate.year)
        : `${formatYearName(startOnDate)} - ${formatYearName(endOnDate) || ''} ${startOnDate.year}`;
    }
    default:
      return `${formatMonthDay(startOnDate, showYear)} - ${formatMonthDay(endOnDate, showYear) || ''}`;
  }
};
